import { useEffect } from "react";

import { Column, Row } from "@tanstack/react-table";
import { get, isNull, isPlainObject } from "lodash-es";

import { EnumOptionsBE } from "src/api/flowTypes";
import { DatasetRow, DesiredType } from "src/api/types";
import { CaseCell } from "src/datasets/components/CaseCell";
import { DefaultInputCell } from "src/datasets/components/DefaultInputCell";
import { EnumPicker } from "src/datasets/components/EnumPicker";
import { FileCell } from "src/datasets/components/FileCell";
import { FloatingEntityEditor } from "src/datasets/components/FloatingEntityEditorCell";
import { JSONEditorCell } from "src/datasets/components/JSONEditorCell";
import { RelationJSONEditorCell } from "src/datasets/components/RelationJSONEditor";
import { SearchableEntityCell } from "src/datasets/components/SearchableEntityCell";
import { SearchableEventCell } from "src/datasets/components/SearchableEventCell";
import { useCellLocalValue } from "src/datasets/components/useCellLocalValue";
import { RowUpdateFunction } from "src/datasets/DatasetTable/DatasetEditTable";
import {
  useDatasetEditTableActions,
  useIsEditingCell,
  useIsSelectedCell,
} from "src/datasets/DatasetTable/stores";
import { JSONValue } from "src/datasets/DatasetTable/types";
import {
  CellId,
  ENTITY_ID_FIELD_NAME,
  isEntityIdColumn,
  isEntityPrimaryOrSecondaryProperty,
  isEventIdColumn,
  isSensitiveColumn,
  jsonToPon,
  parseJsonOrReturnAsIs,
  ponToJson,
} from "src/datasets/DatasetTable/utils";
import {
  DatasetIntegrationNode,
  DatasetIssue,
  getSelectedValues,
  isCaseInputColumn,
} from "src/datasets/utils";
import { useEventListener } from "src/hooks/usehooks-ts";

type EditableCellProps = {
  onChange: RowUpdateFunction;
  value: JSONValue | undefined;
  row: Row<DatasetRow>;
  column: Column<DatasetRow>;
  desiredType: DesiredType;
  columnIssue?: DatasetIssue;
  nullable: boolean;
  integrationNode?: DatasetIntegrationNode;
  required: boolean;
  disabled: boolean;
  cellId: CellId;
  options?: EnumOptionsBE | null;
  relationSchema?: string;
};

export const EditableCell: React.FC<EditableCellProps> = ({
  onChange,
  value: receivedValue,
  row,
  column,
  desiredType,
  columnIssue,
  nullable,
  required,
  integrationNode,
  disabled,
  cellId,
  options: enumValues,
  relationSchema: relation,
}) => {
  const propValue =
    // If the value is undefined and the column is an entity ID column
    // and the parent column value is null, we want to set the visual value of this cell to null
    receivedValue === undefined &&
    isEntityIdColumn(column) &&
    get(row.original, column.parent.id) === null
      ? null
      : receivedValue;

  const selected = useIsSelectedCell(cellId);
  const editing = useIsEditingCell(cellId);
  const { reset } = useDatasetEditTableActions();

  const isCaseEditorCell =
    desiredType === "object" && isCaseInputColumn(column);
  const isRelationJsonEditorCell = "object" === desiredType && relation;
  const isFilesList = desiredType === "files";
  const isJsonEditorCell =
    ["object", "array", "any"].includes(desiredType) &&
    !isRelationJsonEditorCell &&
    !isCaseEditorCell;

  const { value, error, setError, validateAndSave, setLocalValue } =
    useCellLocalValue(
      propValue,
      (value) => {
        // Handle special case for entity fields
        if (isEntityIdColumn(column)) {
          // If this is the entity ID field, and the new value is "None"(null)
          // we empty the whole entity value to null
          if (isNull(value)) {
            onChange(row, column.parent.id, null);
            return;
          }
        }
        onChange(row, column.id, value);
      },
      {
        desiredType,
        nullable,
        required,
        enumValues,
      },
    );

  const isSearchableEntityColumn =
    (isEntityPrimaryOrSecondaryProperty(column) || isEntityIdColumn(column)) &&
    !isSensitiveColumn(column);
  const isSearchableEventColumn = isEventIdColumn(column);

  // Update error state for Entity ID columns
  // on change of the parent column value
  useEffect(() => {
    if (isEntityIdColumn(column)) {
      const parentColumnValue = get(row.original, column.parent.id);

      if (isPlainObject(parentColumnValue)) {
        if (
          !parentColumnValue[ENTITY_ID_FIELD_NAME] &&
          Object.values(parentColumnValue).some((value) => value !== undefined)
        ) {
          setError({
            message: "Entity ID must exist if at least one column is filled",
          });
          return;
        }

        if (
          parentColumnValue[ENTITY_ID_FIELD_NAME] ||
          Object.values(parentColumnValue).every((value) => value === undefined)
        ) {
          setError(undefined);
        }
      }
    }
  }, [row.original, column, setError]);

  useEventListener("paste", (e) => {
    if (isFilesList || !selected || editing) return;
    e.preventDefault();

    const newValue = e.clipboardData?.getData("text");

    if (newValue) {
      validateAndSave(newValue);
    }
  });

  useEventListener("copy", (e) => {
    if (isFilesList || !selected || editing) return;
    e.preventDefault();

    e.clipboardData?.setData("text", ponToJson(value));
  });

  useEventListener("cut", (e) => {
    if (isFilesList || !selected || editing) return;
    e.preventDefault();

    const valueToCopy = ponToJson(value);

    if (e.clipboardData) {
      e.clipboardData.setData("text", valueToCopy);

      validateAndSave("");
    }
  });

  const onChangeHandler = validateAndSave;

  if (isRelationJsonEditorCell) {
    // Floating entity editor requires more work to be done
    // so we're hiding it behind a feature flag for now
    // and actual value must be a plain object, otherwise we can show
    // normal JSON editor, for now it is RelationJSONEditorCell
    if (isPlainObject(propValue)) {
      return (
        <FloatingEntityEditor
          column={column}
          disabled={disabled}
          editing={editing}
          error={error}
          invalid={!!(error && value)}
          propValue={propValue as Record<string, JSONValue>}
          relationSchema={relation}
          row={row}
          selected={selected}
          type={Array.isArray(propValue) ? "array" : typeof propValue}
          value={value}
          warning={columnIssue?.issueType === "type-mismatch"}
          onChange={onChange}
        />
      );
    } else {
      return (
        <RelationJSONEditorCell
          column={column}
          disabled={disabled}
          editing={editing}
          integrationNode={integrationNode}
          invalid={!!(error && value)}
          propValue={propValue}
          relationSchema={relation}
          rowIndex={row.index}
          selected={selected}
          type={Array.isArray(propValue) ? "array" : typeof propValue}
          value={value}
          warning={columnIssue?.issueType === "type-mismatch"}
          onChange={onChangeHandler}
        />
      );
    }
  }

  if (isFilesList) {
    return (
      <FileCell
        column={column}
        disabled={disabled}
        propValue={propValue}
        row={row}
        selected={selected}
        warning={columnIssue?.issueType === "type-mismatch"}
        onChange={onChange}
      />
    );
  }

  if (isJsonEditorCell) {
    return (
      <JSONEditorCell
        column={column}
        disabled={disabled}
        editing={editing}
        integrationNode={integrationNode}
        invalid={!!(error && value)}
        isOpen={editing}
        postfix={
          !!enumValues?.length && (
            <EnumPicker
              options={enumValues}
              selectedValues={getSelectedValues(propValue)}
              onSelect={(selectedValue) => {
                // Handle multiselect for tags
                if (desiredType === "array") {
                  const parsedCellValue = parseJsonOrReturnAsIs(
                    ponToJson(value),
                  );
                  const valueToAdd = JSON.parse(selectedValue);
                  if (Array.isArray(parsedCellValue)) {
                    const updatedValue = parsedCellValue.includes(valueToAdd)
                      ? parsedCellValue.filter((item) => item !== valueToAdd)
                      : [...parsedCellValue, valueToAdd];
                    const newValue = jsonToPon(JSON.stringify(updatedValue));

                    onChangeHandler(newValue);
                  } else {
                    const newValue = jsonToPon(JSON.stringify([valueToAdd]));
                    onChangeHandler(newValue);
                  }
                  return;
                }

                onChangeHandler(selectedValue);
              }}
            />
          )
        }
        rowIndex={row.index}
        selected={selected}
        type={Array.isArray(propValue) ? "array" : typeof propValue}
        value={value}
        warning={columnIssue?.issueType === "type-mismatch"}
        onChange={onChangeHandler}
        onClose={() => reset("editingCellId")}
      />
    );
  }

  if (isCaseEditorCell) {
    return (
      <CaseCell
        column={column}
        disabled={disabled}
        editing={editing}
        error={error}
        isOpen={editing}
        rowIndex={row.index}
        selected={selected}
        value={value}
        warning={columnIssue?.issueType === "type-mismatch"}
        onChange={onChangeHandler}
      />
    );
  }

  // If the column is searchable, we want to use the searchable cell
  if (isSearchableEntityColumn) {
    return (
      <SearchableEntityCell
        column={column}
        desiredType={desiredType}
        disabled={disabled}
        editing={editing}
        error={error}
        isOpen={editing}
        postfix={
          !!enumValues?.length && (
            <EnumPicker
              options={enumValues}
              selectedValues={getSelectedValues(propValue)}
              onSelect={(selectedValue) => {
                // Handle multiselect for tags
                if (desiredType === "array") {
                  const parsedCellValue = parseJsonOrReturnAsIs(
                    ponToJson(value),
                  );
                  const valueToAdd = JSON.parse(selectedValue);
                  if (Array.isArray(parsedCellValue)) {
                    const updatedValue = parsedCellValue.includes(valueToAdd)
                      ? parsedCellValue.filter((item) => item !== valueToAdd)
                      : [...parsedCellValue, valueToAdd];
                    const newValue = jsonToPon(JSON.stringify(updatedValue));

                    validateAndSave(newValue);
                  } else {
                    const newValue = jsonToPon(JSON.stringify([valueToAdd]));
                    validateAndSave(newValue);
                  }
                  return;
                }

                validateAndSave(selectedValue);
              }}
            />
          )
        }
        selected={selected}
        value={value}
        warning={columnIssue?.issueType === "type-mismatch"}
        onChange={onChangeHandler}
        onChangeEntityValue={(value: JSONValue) =>
          onChange(row, column.parent.id, value)
        }
        onResetError={() => setError(undefined)}
        onSetLocalValue={setLocalValue}
      />
    );
  }

  if (isSearchableEventColumn) {
    return (
      <SearchableEventCell
        column={column}
        desiredType={desiredType}
        disabled={disabled}
        editing={editing}
        error={error}
        isOpen={editing}
        postfix={undefined}
        selected={selected}
        value={value}
        warning={false}
        onChange={onChangeHandler}
        onChangeEventValue={(value) => {
          onChange(row, column.parent.id, value);
        }}
        onResetError={() => setError(undefined)}
        onSetLocalValue={setLocalValue}
      />
    );
  }

  return (
    <DefaultInputCell
      desiredType={desiredType}
      disabled={disabled}
      editing={editing}
      error={error}
      postfix={
        !!enumValues?.length && (
          <EnumPicker
            options={enumValues}
            selectedValues={getSelectedValues(propValue)}
            onSelect={(selectedValue) => {
              // Handle multiselect for tags
              if (desiredType === "array") {
                const parsedCellValue = parseJsonOrReturnAsIs(ponToJson(value));
                const valueToAdd = JSON.parse(selectedValue);
                if (Array.isArray(parsedCellValue)) {
                  const updatedValue = parsedCellValue.includes(valueToAdd)
                    ? parsedCellValue.filter((item) => item !== valueToAdd)
                    : [...parsedCellValue, valueToAdd];
                  const newValue = jsonToPon(JSON.stringify(updatedValue));

                  onChangeHandler(newValue);
                } else {
                  const newValue = jsonToPon(JSON.stringify([valueToAdd]));
                  onChangeHandler(newValue);
                }
                return;
              }

              onChangeHandler(selectedValue);
            }}
          />
        )
      }
      selected={selected}
      value={value}
      warning={columnIssue?.issueType === "type-mismatch"}
      onChange={onChangeHandler}
      onResetError={() => setError(undefined)}
      onSetLocalValue={setLocalValue}
    />
  );
};
